import clsx from "clsx";
import { Check } from "lucide-react";
import {
  cloneElement,
  createContext,
  isValidElement,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
  type RefObject,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

import { ESCAPE } from "./escapeStack";
import { type Origin, Motion, Presence, useClosing } from "./presence";
import { type BlockableProps, type IconProp, renderIcon, SectionLabel } from "./ui";
import { useDismiss } from "./useDismiss";

/** Rang d'un menu ouvert dans la pile d'Échap partagée (`useDismiss`) : au-dessus d'un tracé ou d'une sélection,
 *  sous une fenêtre de confirmation. */
export const MENU_ESCAPE = ESCAPE.menu;

const MenuContext = createContext<{ close: () => void } | null>(null);

/** Entrées d'un menu qui se parcourent au clavier (désactivées exclues, bloquées gardées : leur clic explique). */
const ITEMS = '[role="menuitem"]:not(:disabled), [role="menuitemradio"]:not(:disabled), [role="menuitemcheckbox"]:not(:disabled)';

/** Menu déroulant (R9) : coque `rounded-xl p-1 shadow-float`, entrée depuis son bouton (`Presence` + `Motion`),
 *  plan z-30. Il se ferme par Échap (pile partagée : un appui ne ferme que lui), par un clic dehors ou par un
 *  choix — jamais quand la souris le quitte — et se parcourt aux flèches, Début et Fin (Entrée active l'entrée).
 *  À l'ouverture, la première entrée a le focus ; à la fermeture, le focus revient où il était.
 *
 *  Tab ferme le menu et rend le focus à l'élément d'avant (son bouton) : il ne se promène pas dans un menu qui
 *  s'efface.
 *
 *  Deux usages :
 *  - avec `trigger` (recommandé) : le menu enveloppe son bouton dans un conteneur `relative` ; un clic sur le
 *    bouton ouvert le referme proprement (il n'est pas « dehors ») ; le bouton reçoit `aria-haspopup="menu"`,
 *    `aria-expanded` et `aria-controls` (sauf s'il les porte déjà) — il doit les reporter sur son élément
 *    (`Button`, `IconButton` et les balises natives le font) ;
 *  - sans `trigger` : la coque seule, positionnée par rapport au plus proche parent `relative` ; un clic hors de
 *    la coque la ferme (bouton compris : l'ouvrir au `onClick` le rouvrirait aussitôt — préférer `trigger`). Le
 *    bouton porte alors lui-même `aria-haspopup="menu"` et `aria-expanded={open}`.
 *
 *  `side` : sous le bouton (`bottom`, défaut), au-dessus (`top`, pied de barre latérale) ou à droite (`right`, rail
 *  replié) ; `align` : bord commun avec le bouton, `start` (gauche ou haut) ou `end` ; `width` en pixels (320 par
 *  défaut), bornée par la fenêtre.
 *
 *  `portal` (avec `trigger`) : le menu est rendu au-dessus de tout (portail, position fixe calculée sur le bouton,
 *  plan z-100 comme la liste de `Select`), pour ne pas être rogné par un parent qui défile ou coupe ce qui dépasse
 *  (panneau flottant, barre latérale, fenêtre) ; il suit son bouton au défilement et se ferme si la fenêtre change
 *  de taille. */
export function Menu({
  open,
  onClose,
  trigger,
  align = "start",
  side = "bottom",
  width = 320,
  ariaLabel,
  autoFocus = true,
  portal = false,
  wrapperClassName,
  children,
  className,
}: {
  open: boolean;
  onClose: () => void;
  trigger?: ReactNode;
  align?: "start" | "end";
  side?: "bottom" | "top" | "right";
  width?: number;
  ariaLabel?: string;
  /** Met la première entrée au point à l'ouverture (désactiver pour un menu qui commence par un champ). */
  autoFocus?: boolean;
  portal?: boolean;
  /** Mise en page du conteneur du bouton (`relative inline-flex` par défaut ; `flex w-full` pour un bouton pleine
   *  largeur, par exemple). */
  wrapperClassName?: string;
  children: ReactNode;
  /** Marges seulement. */
  className?: string;
}) {
  const wrapper = useRef<HTMLDivElement>(null);
  const id = useId();
  const popup = (
    <Presence show={open}>
      {open && (
        <MenuPopup
          id={id}
          onClose={onClose}
          anchor={trigger !== undefined ? wrapper : null}
          portal={portal && trigger !== undefined}
          align={align}
          side={side}
          width={width}
          ariaLabel={ariaLabel}
          autoFocus={autoFocus}
          className={className}
        >
          {children}
        </MenuPopup>
      )}
    </Presence>
  );
  if (trigger === undefined) return popup;
  return (
    <div ref={wrapper} className={clsx("relative", wrapperClassName ?? "inline-flex")}>
      {menuTrigger(trigger, open, id)}
      {popup}
    </div>
  );
}

/** Bouton d'ouverture renseigné pour les lecteurs d'écran : `aria-haspopup`, `aria-expanded`, `aria-controls`
 *  (les valeurs déjà posées par l'appelant sont gardées). */
export function menuTrigger(trigger: ReactNode, open: boolean, id: string): ReactNode {
  if (!isValidElement<Record<string, unknown>>(trigger)) return trigger;
  const props = trigger.props;
  return cloneElement(trigger, {
    "aria-haspopup": props["aria-haspopup"] ?? "menu",
    "aria-expanded": props["aria-expanded"] ?? open,
    "aria-controls": props["aria-controls"] ?? (open ? id : undefined),
  });
}

const SIDE_CLASS: Record<"bottom" | "top" | "right", Record<"start" | "end", string>> = {
  bottom: { start: "top-full left-0 mt-1", end: "top-full right-0 mt-1" },
  top: { start: "bottom-full left-0 mb-1", end: "bottom-full right-0 mb-1" },
  right: { start: "top-0 left-full ml-1", end: "bottom-0 left-full ml-1" },
};
const SIDE_ORIGIN: Record<"bottom" | "top" | "right", Origin> = { bottom: "top", top: "bottom", right: "left" };

/** Position fixe d'un menu en portail, calculée sur le rectangle de son bouton. `natural` : hauteur du menu sans
 *  borne (mesurée une fois rendu) ; un menu qui ne tient pas sous son bouton s'ouvre au-dessus quand la place y est
 *  plus grande, et sa hauteur est bornée à la place disponible (il défile) : aucune entrée hors de l'écran. */
function fixedPlacement(
  rect: DOMRect,
  side: "bottom" | "top" | "right",
  align: "start" | "end",
  width: number,
  natural?: number,
) {
  const margin = 8;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const clampLeft = (left: number) => Math.max(margin, Math.min(left, vw - margin - Math.min(width, vw - 2 * margin)));
  if (side === "right")
    return align === "start"
      ? { left: clampLeft(rect.right + 4), top: rect.top, maxHeight: vh - rect.top - margin }
      : { left: clampLeft(rect.right + 4), bottom: vh - rect.bottom, maxHeight: rect.bottom - margin };
  const left = clampLeft(align === "start" ? rect.left : rect.right - width);
  const below = vh - rect.bottom - 4 - margin;
  const above = rect.top - 4 - margin;
  const wanted = natural ?? 0;
  const down = side === "bottom" ? wanted <= below || below >= above : !(wanted <= above || above >= below);
  return down
    ? { left, top: rect.bottom + 4, maxHeight: Math.max(below, 120) }
    : { left, bottom: vh - rect.top + 4, maxHeight: Math.max(above, 120) };
}

function MenuPopup({
  id,
  onClose,
  anchor,
  portal,
  align,
  side,
  width,
  ariaLabel,
  autoFocus,
  className,
  children,
}: {
  id: string;
  onClose: () => void;
  anchor: RefObject<HTMLDivElement | null> | null;
  portal: boolean;
  align: "start" | "end";
  side: "bottom" | "top" | "right";
  width: number;
  ariaLabel?: string;
  autoFocus: boolean;
  className?: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const closing = useClosing();
  // Clic dehors : hors du bouton et du menu (le menu en portail n'est pas dans le conteneur du bouton).
  const inside = useMemo(() => (anchor ? [anchor, ref] : [ref]), [anchor]);
  useDismiss(!closing, onClose, inside);

  // Menu en portail : position fixe sur le bouton, suivie au défilement ; fermeture si la fenêtre change de taille.
  // Position initiale calculée au premier rendu : le menu est visible (et sa première entrée focalisable) d'emblée.
  const [placement, setPlacement] = useState<ReturnType<typeof fixedPlacement> | null>(() => {
    const rect = portal ? anchor?.current?.getBoundingClientRect() : undefined;
    return rect ? fixedPlacement(rect, side, align, width) : null;
  });
  useLayoutEffect(() => {
    if (!portal || !anchor) return;
    const place = () => {
      const rect = anchor.current?.getBoundingClientRect();
      if (rect) setPlacement(fixedPlacement(rect, side, align, width, ref.current?.scrollHeight));
    };
    place();
    const onScroll = (e: Event) => {
      if (!ref.current?.contains(e.target as Node)) place();
    };
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onClose);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onClose);
    };
  }, [portal, anchor, side, align, width, onClose]);

  // Focus : première entrée à l'ouverture (comme un menu natif), retour à l'élément d'avant à la fermeture si le
  // focus était resté dans le menu.
  const previousFocus = useRef<HTMLElement | null>(null);
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    previousFocus.current = previous;
    if (autoFocus) ref.current?.querySelector<HTMLElement>(ITEMS)?.focus({ preventScroll: true });
    const menu = ref.current;
    return () => {
      const active = document.activeElement;
      if (!active || active === document.body || menu?.contains(active)) previous?.focus?.({ preventScroll: true });
    };
  }, [autoFocus]);

  const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    // Appui déjà traité par un contrôle du menu (flèches d'un Segmented) : le focus reste où ce contrôle l'a mis.
    if (e.defaultPrevented) return;
    // Tab ferme le menu et rend le focus au bouton (sinon il passerait d'une entrée à l'autre du menu qui sort).
    if (e.key === "Tab") {
      e.preventDefault();
      previousFocus.current?.focus?.({ preventScroll: true });
      onClose();
      return;
    }
    if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(e.key)) return;
    const items = [...(ref.current?.querySelectorAll<HTMLElement>(ITEMS) ?? [])];
    if (!items.length) return;
    e.preventDefault();
    const i = items.indexOf(document.activeElement as HTMLElement);
    const next =
      e.key === "Home" ? 0 : e.key === "End" ? items.length - 1 : (i + (e.key === "ArrowDown" ? 1 : -1) + items.length) % items.length;
    items[i < 0 && e.key === "ArrowUp" ? items.length - 1 : next]?.focus();
  };

  const menu = (
    <MenuContext.Provider value={{ close: onClose }}>
      <Motion
        ref={ref}
        id={id}
        origin={SIDE_ORIGIN[side]}
        role="menu"
        aria-label={ariaLabel}
        onKeyDown={onKeyDown}
        className={clsx(
          "max-h-[min(70vh,480px)] overflow-y-auto overscroll-contain rounded-xl border border-line bg-surface p-1 shadow-float",
          portal ? "fixed z-[100]" : clsx("absolute z-30", SIDE_CLASS[side][align]),
          className,
        )}
        style={{
          width,
          maxWidth: "calc(100vw - 16px)",
          ...(portal && placement
            ? { ...placement, maxHeight: `min(70vh, 480px, ${Math.round(placement.maxHeight)}px)` }
            : null),
        }}
      >
        {children}
      </Motion>
    </MenuContext.Provider>
  );
  return portal ? createPortal(menu, document.body) : menu;
}

/** Entrée de menu (R9) : 32 px au moins, coins de 6 px, icône de 14 px, libellé en 12 px qui passe à la ligne au
 *  besoin, `hint` en seconde ligne grise, `trailing` à droite (`Kbd`, compteur). Le choix appelle `onSelect` puis
 *  ferme le menu (sauf `keepOpen`). `href` : lien (`download` pour un téléchargement). `danger` : action
 *  destructive en rouge. `selected` : entrée d'un choix exclusif (coche à droite, rôle `menuitemradio`).
 *  `blocked` + `onBlocked` : préalable manquant (le clic explique, le menu reste ouvert) ; `disabled` : requête en
 *  cours ou rien à faire. */
export function MenuItem({
  icon,
  label,
  hint,
  onSelect,
  href,
  download,
  target,
  danger = false,
  blocked,
  onBlocked,
  disabled,
  trailing,
  selected,
  title,
  keepOpen = false,
  ...rest
}: BlockableProps & {
  icon?: IconProp;
  label: ReactNode;
  hint?: ReactNode;
  onSelect?: () => void;
  href?: string;
  download?: string | boolean;
  target?: string;
  danger?: boolean;
  disabled?: boolean;
  trailing?: ReactNode;
  selected?: boolean;
  title?: string;
  keepOpen?: boolean;
  [data: `data-${string}`]: string | undefined;
}) {
  const menu = useContext(MenuContext);
  const className = clsx(
    "flex min-h-8 w-full items-center gap-2 rounded-md px-2 py-1 text-left text-ui transition-colors disabled:opacity-40",
    danger ? "text-danger" : "text-ink",
    blocked ? "opacity-40" : danger ? "hover:bg-danger-soft focus-visible:bg-danger-soft" : "hover:bg-hover focus-visible:bg-hover",
  );
  const body = (
    <>
      {icon !== undefined && (
        <span className={clsx("flex w-4 shrink-0 justify-center", danger ? "text-danger" : "text-ink-muted")}>{renderIcon(icon, 14)}</span>
      )}
      <span className="min-w-0 flex-1">
        <span className="block leading-snug break-words">{label}</span>
        {hint && <span className="block text-meta leading-snug break-words text-ink-muted">{hint}</span>}
      </span>
      {trailing}
      {selected !== undefined && <Check size={14} aria-hidden className={clsx("shrink-0 text-accent", !selected && "invisible")} />}
    </>
  );
  const role = selected !== undefined ? "menuitemradio" : "menuitem";
  const choose = (event: ReactMouseEvent<HTMLElement>) => {
    if (blocked) {
      event.preventDefault();
      onBlocked?.(event);
      return;
    }
    onSelect?.();
    if (!keepOpen) menu?.close();
  };
  if (href !== undefined)
    return (
      <a
        {...rest}
        role={role}
        aria-checked={selected}
        aria-disabled={blocked || undefined}
        href={href}
        download={download}
        target={target}
        title={title}
        onClick={choose}
        className={className}
      >
        {body}
      </a>
    );
  return (
    <button
      {...rest}
      type="button"
      role={role}
      aria-checked={selected}
      aria-disabled={blocked || undefined}
      disabled={disabled}
      title={title}
      onClick={choose}
      className={className}
    >
      {body}
    </button>
  );
}

/** Séparateur entre deux groupes d'entrées : seul filet autorisé dans un menu (R4). */
export function MenuSeparator() {
  return <div role="separator" className="my-1 h-px bg-line" />;
}

/** Titre d'un groupe d'entrées, en petites capitales (`SectionLabel inset`). */
export function MenuLabel({ children, trailing }: { children: ReactNode; trailing?: ReactNode }) {
  return (
    <SectionLabel inset trailing={trailing} className="px-2 pt-2 pb-1">
      {children}
    </SectionLabel>
  );
}
